import React, { useCallback, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Icon } from '@ontruck/orc';
import ActionsMenu from './ActionsMenu';
import styled from 'styled-components';

import Status from 'components/orders/ShipmentCard/Status';
import { isEmpty } from 'utils/types';
import Vehicles from './Vehicles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions as ordersActions } from 'state/orders';
import Tooltip from 'components/Tooltip';
import ShipmentCardIconButton from './ShipmentCardIconButton';
import { CalendarIcon, SettingsIcon } from 'refactored/ui/icons';
import { IS_TENANT } from 'tenant/utils';

const LeftColumn = styled.span`
  height: 24px;

  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  padding-right: 8px;
  padding-left: 4px;
  /* identical to box height, or 150% */

  /* grey / 700 */

  color: #54708c;
`;

const RightColumn = styled.span`
  /* #time-window-PU */

  /* width: 300px; */

  /* 16px/regular

Shipper App Default
*/
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  /* identical to box height, or 150% */

  /* grey / 700 */

  color: #54708c;

  /* Inside auto layout */

  flex: none;
  order: 1;
  flex-grow: 1;
`;

const COPIED_TOOLTIP_SHOW_SECS = 3;

const ShipmentCard = ({
  className,
  order,
  dataPickup,
  locationPickup,
  dataDropoff,
  locationDropoff,
  cargoManifests,
  numberOfStops,
  datePickup,
  dateDropoff,
  shipmentsRefs,
  pendingAppointment,
  dateAppointment,
  type,
}) => {
  const { t } = useTranslation('ShipmentCard');
  const actionsBtnRef = useRef(null);
  const dispatch = useDispatch();
  const [actionsMenuOpened, setActionsMenuOpened] = useState(false);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  const { id: orderId } = order;

  const toggleActionsMenu = useCallback(() => {
    setActionsMenuOpened(!actionsMenuOpened);
  }, [actionsMenuOpened]);

  const openOrder = (e) => {
    if (!actionsBtnRef.current.contains(e.target)) {
      dispatch(ordersActions.openOrder({ orderId, type }));
    }
  };

  const { recurring_order: isRecurring } = order;
  const refLabel =
    !isEmpty(shipmentsRefs) && shipmentsRefs[0] && !isEmpty(shipmentsRefs[0])
      ? t('ref_label', {
          orderId,
          internalReference: shipmentsRefs.join(','),
        })
      : t('ref_label_short', { orderId });

  const timesToShowOnDropoff = pendingAppointment ? (
    <span className="pending_appointment">{t('pending_appointment')}</span>
  ) : dateAppointment ? (
    <>
      <CalendarIcon />
      &nbsp;
      {` ${dateAppointment}`}
    </>
  ) : (
    dateDropoff
  );

  const onUrlCopied = useCallback(() => {
    setShowCopiedTooltip(true);
    setTimeout(
      () => setShowCopiedTooltip(false),
      COPIED_TOOLTIP_SHOW_SECS * 1000
    );
  }, []);

  const data = order;

  return (
    <div
      data-e2e={`so-${orderId}`}
      className={className}
      onClick={openOrder}
      role="presentation"
    >
      <div className="tooltipContainer">
        <Tooltip
          position={'top'}
          component={t('Communications:copied_to_clipboard')}
          alwaysVisible={showCopiedTooltip}
        />
      </div>
      <ShipmentCardIconButton
        className="more-actions"
        type="button"
        onClick={toggleActionsMenu}
        ref={actionsBtnRef}
        opened={actionsMenuOpened}
      >
        <SettingsIcon />
      </ShipmentCardIconButton>
      {/* edit & cancel icons future position (className="share-actions")*/}
      <div className="status-wrap">
        <div className="ref">
          <div title={refLabel}>{refLabel}</div>
          {isRecurring && (
            <Icon glyph="recurring" size="24px" color="grey700" />
          )}
        </div>
        <Status order={order} />
        {order?.status === 'ASSIGNED' && (
          <Vehicles vehicles={order?.vehicles} />
        )}
      </div>
      <div className="stops-info">
        <div className="pickup">
          <span title={datePickup} className="stop-date">
            {datePickup}
          </span>

          <h3 title={dataPickup} className="stop-data">
            {dataPickup}
          </h3>
          <span title={locationPickup} className="stop-location">
            {locationPickup}
          </span>
        </div>
        <div
          className={`dropoff ${dateAppointment ? 'dropoff-appointment' : ''}`}
        >
          <span title={dateDropoff} className="stop-date">
            {timesToShowOnDropoff}
          </span>

          <h3 title={dataDropoff} className="stop-data">
            {dataDropoff}
          </h3>
          <span title={locationDropoff} className="stop-location">
            {locationDropoff}
          </span>
        </div>
      </div>
      {IS_TENANT && (
        <div className="custom-references">
          <div className="custom-reference">
            {' '}
            <LeftColumn>Booking:</LeftColumn>
            <RightColumn>{data?.booking_number} |</RightColumn>
            <span></span>
            <LeftColumn>Exp. OL:</LeftColumn>
            <RightColumn>{data?.logistics_operator_code} |</RightColumn>
            <LeftColumn>Ref. Emisor:</LeftColumn>
            <RightColumn>
              {data?.release_container_provider_reference
                ? data?.release_container_provider_reference
                : data?.acceptance_container_provider_reference}
            </RightColumn>
          </div>
          <div className="custom-reference">
            <LeftColumn>Contenedor:</LeftColumn>
            <RightColumn>{data?.reference_number} |</RightColumn>
            <LeftColumn>Precinto:</LeftColumn>
            <RightColumn>{data?.seal_number}</RightColumn>
          </div>
        </div>
      )}
      {actionsMenuOpened && (
        <ActionsMenu
          handleOptionClick={toggleActionsMenu}
          onClickOutside={toggleActionsMenu}
          cargoManifests={cargoManifests}
          numberOfStops={numberOfStops}
          openOrder={openOrder}
          order={order}
          onUrlCopied={onUrlCopied}
          type={type}
        />
      )}
    </div>
  );
};

ShipmentCard.propTypes = {
  className: PropTypes.string,
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  dataPickup: PropTypes.string,
  locationPickup: PropTypes.string,
  dataDropoff: PropTypes.string,
  locationDropoff: PropTypes.string,
  cargoManifests: PropTypes.string,
  numberOfStops: PropTypes.number,
  datePickup: PropTypes.shape({
    date: PropTypes.string,
    full: PropTypes.string,
    times: PropTypes.string,
  }),
  dateDropoff: PropTypes.shape({
    date: PropTypes.string,
    full: PropTypes.string,
    times: PropTypes.string,
  }),
  openOrder: PropTypes.func.isRequired,
  type: PropTypes.string,
  shipmentsRefs: PropTypes.arrayOf(PropTypes.string),
};

ShipmentCard.defaultProps = {
  className: '',
  order: {},
  dataPickup: '',
  locationPickup: '',
  dataDropoff: '',
  locationDropoff: '',
  cargoManifests: [],
  numberOfStops: 0,
  datePickup: {},
  dateDropoff: {},
  type: '',
  shipmentsRefs: [],
};
export default ShipmentCard;
